/// <amd-module name="Build/Admin/Common/Actions/NavigationActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";

import { QueryParams } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";

export interface INavigationActionCreatorOptions {
    pageContext: IVssPageContext;
}

export class NavigationActionCreator {
    private _pageContext: IVssPageContext;
    private _historyService: IVssHistoryService;

    constructor(options: INavigationActionCreatorOptions) {
        this._pageContext = options.pageContext;
        this._historyService = options.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    public validateQueryParameterValues(): void {
        const queueId = this.getQueueIdParam();
        const agentId = this.getAgentIdParam();

        if ((queueId !== undefined && queueId <= 0)
            || (agentId !== undefined && agentId <= 0)) {
            // If the data for the view is bad, step out to the next best view
            const linkingService = this._pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            const agentQueuesLink: string = linkingService.getProjectPoolsUrl();
            FastPageSwitch(this._pageContext, agentQueuesLink, false);
        }
    }

    public replaceQueryParameters(queueId: number | undefined, poolId: number | undefined, tabId: string | undefined, agentId: number | undefined): void {
        const currentState = this._historyService.getState();

        if (queueId) {
            currentState[QueryParams.QueueId] = queueId.toString();
        }
        else {
            delete currentState[QueryParams.QueueId];
        }

        if (poolId && !queueId) {
            currentState[QueryParams.PoolId] = poolId.toString();
        }
        else {
            delete currentState[QueryParams.PoolId];
        }

        if (tabId) {
            currentState[QueryParams.TabId] = tabId;
        }
        else {
            delete currentState[QueryParams.TabId];
        }

        if (agentId) {
            currentState[QueryParams.AgentId] = agentId.toString();
        }
        else {
            delete currentState[QueryParams.AgentId];
        }

        this._historyService.replaceState(currentState);
    }

    public getTabIdParam(): string | undefined {
        const currentState = this._historyService.getState();
        return currentState[QueryParams.TabId];
    }

    public setTabIdParam(tabId: string): void {
        const currentState = this._historyService.getState();
        currentState[QueryParams.TabId] = tabId;
        this._historyService.pushState(currentState);
    }

    public getQueueIdParam(): number | undefined {
        const currentState = this._historyService.getState();
        if (currentState[QueryParams.QueueId]) {
            return parseInt(currentState[QueryParams.QueueId], 10);
        }
        else {
            return undefined;
        }
    }

    public setQueueIdParam(queueId: number | undefined): void {
        const currentState = this._historyService.getState();
        if (queueId) {
            currentState[QueryParams.QueueId] = queueId.toString();
        }
        else {
            delete currentState[QueryParams.QueueId];
        }
        this._historyService.pushState(currentState);
    }

    public getAgentIdParam(): number | undefined {
        const currentState = this._historyService.getState();
        if (currentState[QueryParams.AgentId]) {
            return parseInt(currentState[QueryParams.AgentId], 10);
        }
        else {
            return undefined;
        }
    }

    public setAgentIdParam(agentId: number | undefined): void {
        const currentState = this._historyService.getState();
        if (agentId) {
            currentState[QueryParams.AgentId] = agentId.toString();
        }
        else {
            delete currentState[QueryParams.AgentId];
        }
        this._historyService.pushState(currentState);
    }

    public getPoolIdParam(): number | undefined {
        const currentState = this._historyService.getState();
        if (currentState[QueryParams.PoolId]) {
            return parseInt(currentState[QueryParams.PoolId], 10);
        }
        else {
            return undefined;
        }
    }

    public setPoolIdParam(poolId: number | undefined): void {
        const currentState = this._historyService.getState();
        if (poolId) {
            currentState[QueryParams.PoolId] = poolId.toString();
        }
        else {
            delete currentState[QueryParams.PoolId];
        }
        this._historyService.pushState(currentState);
    }

    public getAgentCloudIdParam(): number | undefined {
        const currentState: { [key: string]: string } = this._historyService.getState();
        if (currentState[QueryParams.AgentCloudId]) {
            return parseInt(currentState[QueryParams.AgentCloudId], 10);
        }
        else {
            return undefined;
        }
    }

    public setAgentCloudIdParam(agentCloudId: number): void {
        const currentState: { [key: string]: string } = this._historyService.getState();
        if (agentCloudId) {
            currentState[QueryParams.AgentCloudId] = agentCloudId.toString();
        }
        else {
            delete currentState[QueryParams.AgentCloudId];
        }
        this._historyService.pushState(currentState);
    }
}
